import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { getTime } from 'date-fns';
import _isNil from 'lodash/isNil';
import { RootState } from '@Services/redux/Store';
import { getUpdatedElapsedTime } from './Helpers';
import { IPersistedQcPole, IPersistedQcRun } from './Interfaces';
import {
    IBrowserPersistenceState,
    initialBrowserPersistenceState,
} from './State';

const BrowserPersistenceSlice = createSlice({
    name: 'BrowserPersistence',
    initialState: initialBrowserPersistenceState,
    reducers: {
        initializePersistedQcRun: (
            state: IBrowserPersistenceState,
            action: PayloadAction<IPersistedQcRun>
        ) => {
            state.qcRuns.push(action.payload);
        },
        resumeQcRun: (
            state: IBrowserPersistenceState,
            action: PayloadAction<string>
        ) => {
            const qcRun = state.qcRuns.find(
                (r) => r.folder_id === action.payload
            );

            qcRun.lastUpdatedTimestamp = getTime(Date.now());
        },
        updatePersistedQcPole: (
            state: IBrowserPersistenceState,
            action: PayloadAction<{
                folderId: string;
                updatedPole: IPersistedQcPole;
            }>
        ) => {
            const qcRun = state.qcRuns.find(
                (r) => r.folder_id === action.payload.folderId
            );
            if (_isNil(qcRun)) {
                return null;
            }

            // update the pole
            const poleIndex = qcRun.data.findIndex(
                (p) =>
                    p.entity_version_id ===
                    action.payload.updatedPole.entity_version_id
            );

            qcRun.data[poleIndex] = action.payload.updatedPole;

            // update the elapsed time
            qcRun.elapsedTime = getUpdatedElapsedTime(qcRun);
            qcRun.lastUpdatedTimestamp = getTime(Date.now());
        },
        removePersistedQcRun: (
            state: IBrowserPersistenceState,
            action: PayloadAction<string>
        ) => {
            const index = state.qcRuns.findIndex(
                (q) => q.folder_id === action.payload
            );
            if (index > -1) {
                state.qcRuns.splice(index, 1);
            }
        },
        setSelectedQcPole: (
            state: IBrowserPersistenceState,
            action: PayloadAction<{ folderId: string; entityVersionId: string }>
        ) => {
            const qcRun = state.qcRuns.find(
                (r) => r.folder_id === action.payload.folderId
            );

            if (qcRun) {
                qcRun.selectedPoleEntityVersionId =
                    action.payload.entityVersionId;
            }
        },
    },
});

export const BrowserPersistenceActions = BrowserPersistenceSlice.actions;

export const persistedQcRunsState = (state: RootState) =>
    state.browserPersistence.qcRuns;

export default BrowserPersistenceSlice.reducer;
